import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import { getSelectedStoreTenant } from '../selectors/tenant.selectors';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';
import * as PopupsActions from '../actions/popups.actions';
import * as PopupsSelectors from '../selectors/popups.selectors';
import { LaravelPopupsService } from '../../commons/services/laravel/laravel-popups.service';
import { NewPopupsComponent } from '../../modules/home/modules/popups/new-popups/new-popups.component';


@Injectable()
export class PopupEffects {
    error$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(
                PopupsActions.deletePopupFailed,
                PopupsActions.savePopupsFailed
            ),
            tap(({ error }) => {
              if (error) {
                this.alertService.showErrorMessage('Errore', error);
              }
            })
          ),
        { dispatch: false }
    );

    loadPopups$ = createEffect(() =>
      this.actions$.pipe(
        ofType(PopupsActions.loadPopups),
        withLatestFrom(this.store$.select(getSelectedStoreTenant)),
        map(([{ page, perPage, order, direction, filters, includes }, tenant]) => {
          const filter = {...filters};
          if(filters.onlyCurrentTenant){
            filter.tenant_id = tenant.id
          }
          return {page, perPage, order, direction, filters: filter, includes};
        }),
        switchMap(({page, perPage, order, direction, filters, includes}) => {
          return this.popupService
            .list(page, perPage, order, direction, filters, includes)
            .pipe(
              map((result) => {
                return PopupsActions.loadPopupsCompleted({
                    popups: result.data,
                    currentPage: page,
                    total: result.total,
                    perPage,
                    order,
                    direction,
                    filters,
                    includes
                  });
              }),
              catchError((error) =>
                of(PopupsActions.loadPopupsFailed({ error })))
            );
          })
        )
      );

      changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(PopupsActions.changePage),
          withLatestFrom(this.store$.select(PopupsSelectors.getPopupsTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
              PopupsActions.loadPopups({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
        );
    
        changeSort = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.changeSort),
            withLatestFrom(this.store$.select(PopupsSelectors.getPopupsTableState)),
            map(
              ([
                action,
                { total, currentPage, perPage, direction, order, filters, includes },
              ]) =>
                PopupsActions.loadPopups({
                  page: currentPage,
                  perPage: perPage,
                  order: action.order,
                  direction: action.direction,
                  filters,
                  includes,
                })
            )
          )
        );
    
        changeFilters = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.changeFilters),
            withLatestFrom(this.store$.select(PopupsSelectors.getPopupsTableState)),
            map(
              ([
                { filters },
                { total, currentPage, perPage, direction, order, includes },
              ]) =>
                PopupsActions.loadPopups({
                  page: currentPage,
                  perPage: perPage,
                  order,
                  direction,
                  filters,
                  includes,
                })
            )
          )
        );

        editPopup$ = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.editPopup),
            map(({ popup }) => {
              let dialogRef = this.dialog.open(NewPopupsComponent, {
                data: {
                  popup,
                },
                panelClass: 'full-screen-dialog',
              });
              return PopupsActions.popupDialogOpened({
                dialogId: dialogRef.id,
              });
            })
          )
        );

        savePopup$ = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.savePopup),
            withLatestFrom(this.store$.select(getSelectedStoreTenant)),
            mergeMap(([{ popup }, selectedTenant]) => {
              let tenantId = selectedTenant ? selectedTenant.id : null;
              let popupSave = {
                ...popup.toDTO(),
                tenant_id: tenantId
              };
              return this.popupService.upsert(popupSave).pipe(
                  map((result) =>
                    PopupsActions.savePopupCompleted({ popup: result })
                  ),
                tap((action) => {
                  if (action.type === PopupsActions.savePopupCompleted.type) {
                    this.alertService.showConfirmMessage(`Popup salvato con successo`)
                    this.store$.dispatch(PopupsActions.closePopupsDialog());
                  } else {
                    this.store$.dispatch(PopupsActions.savePopupsFailed({ error: Error }));
                  }
                })
              );
            })
          )
        );

        deletePopup$ = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.deletePopup),
            switchMap(({ popup }) =>
              this.alertService
                .showConfirmDialog(
                  'Conferma eliminazione',
                  `Sei sicuro di voler eliminare l'elemento selezionato?`
                )
                .pipe(
                  mergeMap((confirm) => {
                    return confirm
                      ? this.popupService.delete(popup.id).pipe(
                          map(() =>
                            PopupsActions.deletePopupCompleted({
                              popup,
                            })
                          ),
                          catchError((error) =>
                            of(PopupsActions.deletePopupFailed({ error }))
                          ),
                          tap((action) => {
                            if (action.type === PopupsActions.deletePopupCompleted.type) {
                              this.alertService.showConfirmMessage(` Popup eliminato con successo`);
                              this.store$.dispatch(PopupsActions.closePopupsDialog());
                            }
                          })
                        )
                      : of(PopupsActions.deletePopupCancelled());
                  })
                )
            )
          )
        );

        reloadAfterSave = createEffect(() =>
          this.actions$.pipe(
            ofType(PopupsActions.savePopupCompleted),
            withLatestFrom(this.store$.select(PopupsSelectors.getPopupsTableState)),
            map(
              ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                PopupsActions.loadPopups({
                  page: currentPage,
                  perPage,
                  order,
                  direction,
                  filters,
                  includes,
                })
            )
          )
        );
        
        reloadAfterDelete = createEffect(() =>
          this.actions$.pipe(
              ofType(PopupsActions.deletePopupCompleted),
              withLatestFrom(this.store$.select(PopupsSelectors.getPopupsTableState)),
              map(
              ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
                PopupsActions.loadPopups({
                  page: currentPage,
                  perPage,
                  order,
                  direction,
                  filters,
                  includes,
                  })
              )
          )
        );
    
    
        closeSelectionDialog = createEffect(
          () =>
              this.actions$.pipe(
              ofType(PopupsActions.closeSelectionDialog),
              withLatestFrom(this.store$.select(PopupsSelectors.getSelectionDialogId)),
              tap(([_, dialogId]) => {
                  if (dialogId) {
                  this.dialog.getDialogById(dialogId).close();
                  }
              })
              ),
          { dispatch: false }
        );
        
        requestCloseDialog$ = createEffect(() =>
            this.actions$.pipe(
              ofType(PopupsActions.requestClosePopupsDialog),
              switchMap(() => {
                const confirmDialogRef = this.dialog.open(DialogComponent, {
                  disableClose: false
                });
    
                // Imposta il messaggio del dialog
                confirmDialogRef.componentInstance.title = "Chiudi";
                confirmDialogRef.componentInstance.confirmMessage =
                  "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
    
                return confirmDialogRef.afterClosed().pipe(
                  map(result =>
                    result
                      ? PopupsActions.confirmClosePopupsDialog()
                      : PopupsActions.cancelClosePopupsDialog()
                  )
                );
              })
            )
          );
        
        
          confirmCloseDialog$ = createEffect(() =>
            this.actions$.pipe(
              ofType(PopupsActions.confirmClosePopupsDialog),
              map(() => PopupsActions.closePopupsDialog())
            )
          );
        
          cancelCloseDialog$ = createEffect(() =>
            this.actions$.pipe(
              ofType(PopupsActions.cancelClosePopupsDialog),
              map(() => {
                return { type: 'NO_ACTION' };
              })
            )
          );
        
          closeDialog$ = createEffect(
            () =>
              this.actions$.pipe(
                ofType(PopupsActions.closePopupsDialog),
                withLatestFrom(this.store$.select(PopupsSelectors.getPopupDialogId)),
                tap(([_, dialogId]) => {
                  if (dialogId) {
                    this.dialog.getDialogById(dialogId).close();
                  }
                })
              ),
            { dispatch: false }
          );

    constructor(
            private actions$: Actions,
            private store$: Store<AppState>,
            private popupService: LaravelPopupsService,
            private dialog: MatDialog,
            private alertService: AlertService
    ) {}


}